// EditMilestoneDrawer.js
import React, { useState, useEffect } from "react";
import Drawer from "@mui/material/Drawer";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  TextField,
  Button,
  Grid,
  Box,
} from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getUserList } from "../../../api/casp/user";
import { toast } from "react-toastify";
import { Action, UserStatus } from "../../../constants/Common";
import { createPortfolios, updatePortfolios } from "../../../api/casp/portfolio";
import { withTranslation } from "react-i18next";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { getAllDeliverableList } from "../../../api/casp/deliverable";

const AddPortfolioDrawer = ({ isOpen, handleClose, action, selectedPortfolioRow, t }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPortfolioManager, setSelectedPortfolioManager] = useState("");
  const [deliverables, setDeliverables] = useState([]);
  const [portfolioObject, setPortfolioObject] = useState({
    name: "",
    description: "",
    assigneeId: ""
  });

  useEffect(() => {
    if (selectedPortfolioRow !== undefined && action === Action.EDIT) {
      fetchDeliverableList()
      const name = `${selectedPortfolioRow?.assignee?.firstName} ${selectedPortfolioRow?.assignee?.lastName}`
      setSelectedPortfolioManager({
        id: selectedPortfolioRow?.assignee?.id,
        label: name,
        key: `${selectedPortfolioRow.assigneeId}|${name}`,
      });
      formik.setValues({
        ...selectedPortfolioRow,
        name: selectedPortfolioRow?.name?.slice(7),
        assigneeId: selectedPortfolioRow?.assignee?.id,
        deliverables: selectedPortfolioRow?.projectDeliverables?.map((pd) => ({
          deliverableId: pd.deliverableId ?? pd.id,
          deliverableName: pd.name,
          deliverableStartDate: pd.startDate,
          deliverableEndDate: pd.endDate,
        })) ?? [],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolioRow])

  useEffect(() => {
    fetchDeliverableList()
  }, [action])

  const portfolioValidation = Yup.object().shape({
    name: Yup.number()
      .required(t("Please Enter Cohurt Number")),
    description: Yup.string()
      .max(2000, t("Description can not be greater than 2000 characters!"))
      .strict(true)
      .trim(t('Must not include leading and trailing spaces')),
    startDate: Yup.date()
      .typeError(t("Given date is invalid type"))
      .required(t("Please Enter start date")),
    endDate: Yup.date()
      .typeError(t("Given date is invalid type"))
      .required(t("Please Enter end date"))
      .min(Yup.ref("startDate"), t("End date cannot be less than start date")),
    // .required(t("Please Enter description")),
    // assigneeId: Yup.string().required(t("Please select manager"))
    //deliverables Validation
    deliverables: Yup.array().of(
      Yup.object().shape({
        deliverableStartDate: Yup.date()
          .typeError(t("Given date is invalid type"))
          .required(t("Please Enter deliverable start date")),

        deliverableEndDate: Yup.date()
          .typeError(t("Given date is invalid type"))
          .required(t("Please Enter deliverable end date"))
          .min(Yup.ref("deliverableStartDate"), t("End date cannot be less than start date")),
      })
    ),
  });

  const handleCreate = async () => {
    try {
      setIsLoading(true);
      const response = await createPortfolios({
        name: formik.values.name.toString(),
        description: formik.values.description,
        startDate: dayjs(formik.values.startDate).format('YYYY-MM-DD'),
        endDate: dayjs(formik.values.endDate).format('YYYY-MM-DD'),
        // assigneeId: formik.values.assigneeId
        deliverables: formik.values.deliverables.map((d) => ({
          deliverableId: d.deliverableId, // fixed or dynamic depending on UI
          startDate: dayjs(d.deliverableStartDate).format("YYYY-MM-DD"),
          endDate: dayjs(d.deliverableEndDate).format("YYYY-MM-DD"),
        })),
      });
      if (response.statusCode === 200) {
        toast.success(response.data.message);
        setIsLoading(false);
        handleCancel('success');
      } else {
        toast.error(response.data.message);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
      toast.error(err.message);
    }
  };

  const fetchDeliverableList = async (queryParams) => {
    try {
      setIsLoading(true);
      const res = (await getAllDeliverableList()).data.data;

      // Sort deliverables by createdAt (earliest first)
      const sortedData = res.data.sort((a, b) => (a.deliverableOrder ?? 0) - (b.deliverableOrder ?? 0));


      const resultData = await Promise.all(sortedData.map(async (res, idx) => {
        return {
          id: res.id,
          deliverableName: res.name,
          deliverableOrder: res.deliverableOrder
        };
      }));
      setDeliverables(resultData)

      setIsLoading(false);
    } catch (error) {
      console.error("Failed to fetch deliverable list:", error);
      setIsLoading(false);
    }
  }

  const handleEdit = async () => {
    try {
      setIsLoading(true);
      const response = await updatePortfolios(selectedPortfolioRow.id, {
        name: formik.values.name.toString(),
        description: formik.values.description,
        startDate: dayjs(formik.values.startDate).format('YYYY-MM-DD'),
        endDate: dayjs(formik.values.endDate).format('YYYY-MM-DD'),
        // assigneeId: formik.values.assigneeId,
        deliverables: formik.values.deliverables.map((d) => ({
          deliverableId: d.deliverableId, // fixed or dynamic depending on UI
          startDate: dayjs(d.deliverableStartDate).format("YYYY-MM-DD"),
          endDate: dayjs(d.deliverableEndDate).format("YYYY-MM-DD"),
        })),
      });
      if (response.statusCode === 200) {
        toast.success(response.data.message);
        setIsLoading(false);
        handleCancel('success');
      } else {
        toast.error(response.data.message);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
      toast.error(err.message);
    }
  };

  const formik = useFormik({
    initialValues: {
      ...portfolioObject,
      deliverables: deliverables.map((d) => ({
        deliverableId: d.id,
        deliverableName: d.deliverableName,
        deliverableStartDate: null,
        deliverableEndDate: null,
      })),
    },
    validationSchema: portfolioValidation,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const handleCancel = (message, reason) => {
    if (reason === 'backdropClick') {
      return;
    } else if (isOpen) {
      formik.resetForm();
      formik.setValues(formik.initialValues);
    }

    handleClose(message);
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleCancel}>
      <div style={{ width: "500px" }} className="editProjTypeDrawer">
        <List>
          <ListItem>
            <ListItemText>{action === Action.EDIT ? t('Edit Cohort') : t('Add Cohort')}</ListItemText>
            <ListItemIcon>
              <IconButton onClick={handleCancel}>
                <CancelOutlinedIcon />
              </IconButton>
            </ListItemIcon>
          </ListItem>
          <form>
            <ListItem>
              <TextField
                type="number"
                fullWidth
                id="outlined-basic"
                label={t("Enter Cohort Number")}
                name="name"
                value={formik.values?.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isLoading}
                required
              />
            </ListItem>
            <div className="mx-3">
              {formik.touched.name && formik.errors.name && (
                <span className="text-danger">{formik.errors.name}</span>
              )}
            </div>
            <ListItem>
              <TextField
                fullWidth
                id="outlined-basic"
                label={t("Enter Description")}
                type="text"
                name="description"
                value={formik.values?.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isLoading}
                multiline
              />
            </ListItem>
            <div className="mx-3">
              {formik.touched.description && formik.errors.description && (
                <span className="text-danger">{formik.errors.description}</span>
              )}
            </div>

            <Grid container spacing={2} sx={{ px: 3, mt: 1 }}>
              {/* Start Date */}
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={t("Start Date") + " *"}
                    disabled={isLoading}
                    format="MM/DD/YYYY"
                    name="startDate"
                    value={formik?.values?.startDate ? dayjs(formik.values.startDate) : null}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        onBlur: () => formik.setFieldTouched("startDate", true, false),
                      },
                    }}
                    onChange={(e) => {
                      formik.setFieldValue("startDate", e ? e.toDate() : null);
                      if (!formik.touched?.startDate) {
                        formik.setFieldTouched("startDate", true, false);
                      }
                    }}
                    required
                  />
                </LocalizationProvider>
                {formik.touched.startDate && formik.errors.startDate && (
                  <span className="text-danger d-block mt-1">
                    {formik.errors.startDate}
                  </span>
                )}
              </Grid>

              {/* End Date */}
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={t("End Date") + " *"}
                    disabled={isLoading}
                    format="MM/DD/YYYY"
                    name="endDate"
                    // value={
                    //   formik?.values?.endDate?.toString().trim()
                    //     ? dayjs(formik.values.endDate)
                    //     : null
                    // }
                    value={formik?.values?.endDate ? dayjs(formik.values.endDate) : null}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        onBlur: () => formik.setFieldTouched("endDate", true, false),
                      },
                    }}
                    onChange={(e) => {
                      // formik.setFieldValue(
                      //   "endDate",
                      //   e ? dayjs(e).format("YYYY-MM-DD") : ""
                      // );
                      formik.setFieldValue("endDate", e ? e.toDate() : null);
                      if (!formik.touched?.endDate) {
                        formik.setFieldTouched("endDate", true, false);
                      }
                    }}
                    required
                  />
                </LocalizationProvider>
                {formik.touched.endDate && formik.errors.endDate && (
                  <span className="text-danger d-block mt-1">
                    {formik.errors.endDate}
                  </span>
                )}
              </Grid>
            </Grid>
            <ListItemText
              variant="h6"
              sx={{ px: 3, mt: 3, mb: 1 }}
            >
              Deliverables List
            </ListItemText>
            <Grid container spacing={2} sx={{ px: 3, mt: 2 }}>
              {formik?.values?.deliverables?.map((d, index) => (
                <Grid key={d.id} container item spacing={2}>
                  {/* Deliverable name */}
                  <Grid item xs={12}>
                    {d.deliverableName}
                  </Grid>

                  {/* Start Date */}
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Start Date"
                        format="MM/DD/YYYY"
                        value={d.deliverableStartDate ? dayjs(d.deliverableStartDate) : null}
                        onChange={(e) =>
                          formik.setFieldValue(
                            `deliverables[${index}].deliverableStartDate`,
                            e ? e.toDate() : null
                          )
                        }
                      />
                    </LocalizationProvider>
                  </Grid>

                  {/* End Date */}
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="End Date"
                        format="MM/DD/YYYY"
                        value={d.deliverableEndDate ? dayjs(d.deliverableEndDate) : null}
                        onChange={(e) =>
                          formik.setFieldValue(
                            `deliverables[${index}].deliverableEndDate`,
                            e ? e.toDate() : null
                          )
                        }
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              ))}
            </Grid>


            {/* <div className="mx-3 mt-2">
              <SearchDropDownListPaginationComponent
                label={t("Manager") + ' *'}
                disabled={isLoading}
                searchEnabled={false}
                listCall={getUserList}
                defaultValue={action === Action.EDIT ? selectedPortfolioManager : null}
                apiParams={{
                  order: "firstName",
                  direction: "desc",
                  isActive: true,
                  includeAdmins: false,
                  ...(action === Action.EDIT && {
                    currentPortfolioId: parseInt(selectedPortfolioRow.id)
                  })
                }}
                emitItem={(newValue) => {
                  setSelectedPortfolioManager(newValue)
                  formik.setFieldValue(
                    "assigneeId",
                    newValue ? newValue.id : ""
                  );
                }}
                onBlur={() => {
                  if (!formik.touched?.assigneeId) {
                    formik.setFieldTouched("assigneeId", true, true);
                  }
                }}
              />
              {formik.touched.assigneeId &&
                formik.errors.assigneeId && (
                  <span className="text-danger">
                    {formik.errors.assigneeId}
                  </span>
                )}
            </div> */}

            <ListItem className="justify-content-center mt-4">
              <Button
                onClick={action === Action.EDIT ? handleEdit : handleCreate}
                variant="contained"
                className="drawerCommonBtn greenBtn mx-2"
                type="submit"
                disabled={isLoading || !formik.isValid}
              >
                {t("Submit")}
              </Button>
              <Button
                onClick={handleCancel}
                variant="contained"
                className="drawerCommonBtn redButton"
              >
                {t("Cancel")}
              </Button>
            </ListItem>
          </form>
        </List>
      </div>
    </Drawer>
  );
};

export default withTranslation()(AddPortfolioDrawer);
